import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Card } from 'react-bootstrap';
import AppContext from 'Utils/AppContext';
import RunningJobRow from 'Utils/RunningJobRow';
import SortableTh, { sortRows } from 'Utils/SortableTh';
import { engineLabels, EngineIcon, getDisplayDate } from 'Utils';
import { BRAND, INK, STATUS, FONT_MONO, chartSeriesColor } from 'Utils/styleTokens';
import { getDataSourceSummary } from './metaAnalysisApi';
import { startProfileJob } from './profileApi';
import { getDataSourceNumericId } from 'DataSources/datasourcesApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import AdoptedFromBadge from 'DataSources/AdoptedFromBadge';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

const rowsPretty = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};

// Full commercial product name for a matched identity, e.g. "SugarCRM Community" — never
// the bare productName ("Community"), which reads as an incomplete label on its own.
const commercialName = (identity) => {
  const cm = identity && identity.commercialMatch;
  if (!cm) return null;
  return [cm.vendorName, cm.productName].filter(Boolean).join(' ') || null;
};

// Display label for a cluster: its full commercial name when matched, else the raw identity name.
const identityLabel = (identity) => commercialName(identity) || (identity && identity.name) || 'Unnamed';

// Confidence badge per mockup 5b: high-confidence commercial match → "N% MATCH"
// (green); anything else → INFERRED (grey).
const confidenceBadge = (identity) => {
  const confidence = identity?.commercialMatch?.confidence;
  if (confidence != null && confidence >= 0.7) {
    return { label: `${Math.round(confidence * 100)}% MATCH`, color: STATUS.success.text, bg: STATUS.success.bg };
  }
  return { label: 'INFERRED', color: STATUS.neutral.text, bg: STATUS.neutral.bg };
};

/**
 * Meta schema profiling landing — mockup 5b. Identity-cluster cards up top
 * (click → expand the cluster's member data sources inline), then the
 * "Profiled databases — key elements" table with per-DB meta facts and a
 * Profile action (re-runs the metadata profile for that source).
 */
function MetaLandscape({ identities, loading, activeJobs, cancelling, onCancelJob, onProfileStarted }) {
  const { setNotification, connections } = useContext(AppContext) || {};
  const history = useHistory();
  const [summaries, setSummaries] = useState({});
  const [busy, setBusy] = useState({});
  // Live filter for the profiled-databases table (name / engine / host / cluster).
  const [searchTerm, setSearchTerm] = useState('');
  // Header-click sort; null = default order (running scans pinned, then most recently profiled).
  const [tableSort, setTableSort] = useState(null);
  // Which cluster card is expanded to show its member data sources inline (null = none).
  const [expandedClusterId, setExpandedClusterId] = useState(null);

  // Active (PENDING/RUNNING) profile job per platform data-source id, so a database whose meta
  // scan is in flight shows the shared running-row (with Cancel) in place of its normal row.
  const activeJobByDataSource = {};
  (activeJobs || []).forEach((j) => {
    if (j && j.dataSourceId != null && activeJobByDataSource[j.dataSourceId] == null) {
      activeJobByDataSource[j.dataSourceId] = j;
    }
  });

  // The meta-analysis overview carries the WORKER's member ids, which don't
  // reliably match platform data-source ids. Resolve members to platform sources
  // by name (unique per deployment) and only fall back to the raw id.
  const platformIdOf = (member) => {
    const byName = (connections || []).find((c) => c.name === member.name);
    if (byName) {
      const id = getDataSourceNumericId({ id: byName.id, tableData: { id: byName.id } });
      if (id != null) return id;
    }
    const raw = Number(member.dataSourceId);
    return Number.isFinite(raw) ? raw : null;
  };

  // Same name-join as platformIdOf, for provenance (the overview payload doesn't carry origin).
  const provenanceOf = (member) => {
    const c = (connections || []).find((x) => x.name === member.name) || {};
    return { registeredServiceId: c.registeredServiceId || null, registeredServiceName: c.registeredServiceName || null };
  };

  // Per-member meta facts for the profiled-databases table. Fetched once per
  // unique member source (identities are already capped by the overview API).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const targets = [];
      const seen = new Set();
      (identities || []).forEach((identity) => {
        (identity.dataSources || []).forEach((member) => {
          const platformId = platformIdOf(member);
          if (platformId == null || seen.has(member.dataSourceId)) return;
          seen.add(member.dataSourceId);
          targets.push([member.dataSourceId, platformId]);
        });
      });
      const results = await Promise.allSettled(
        targets.slice(0, 24).map(([memberId, platformId]) =>
          getDataSourceSummary(platformId).then((s) => [memberId, s])
        )
      );
      if (cancelled) return;
      const bySource = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled' && r.value) bySource[r.value[0]] = r.value[1];
      });
      setSummaries(bySource);
    };
    if (identities && identities.length > 0) load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identities, connections]);

  const run = async (member, kind) => {
    const numericId = platformIdOf(member);
    if (numericId == null) return;
    setBusy((prev) => ({ ...prev, [`${kind}-${member.dataSourceId}`]: true }));
    try {
      const gate = await ensureConnectionTested(numericId, {
        history,
        setNotification,
        name: member.name,
      });
      if (!gate.ok) {
        setBusy((prev) => ({ ...prev, [`${kind}-${member.dataSourceId}`]: false }));
        return;
      }
      const job = await startProfileJob(numericId);
      // Show the running row immediately (optimistic); the parent's poll reconciles it.
      if (onProfileStarted && job) {
        onProfileStarted({
          ...job,
          dataSourceId: numericId,
          dataSourceName: member.name,
          engine: member.engine,
          status: job.status || job.state || 'PENDING',
        });
      }
      if (setNotification) setNotification({ header: 'Profile started', body: `Re-profiling ${member.name}.` });
    } catch (e) {
      console.log(`${kind} start failed`, e);
      if (setNotification) setNotification({ header: 'Failed to start', body: e.message || String(e) });
    }
    setBusy((prev) => ({ ...prev, [`${kind}-${member.dataSourceId}`]: false }));
  };

  // member dataSourceId → owning identity, for the CLUSTER column.
  const clusterOf = {};
  (identities || []).forEach((identity, idx) => {
    (identity.dataSources || []).forEach((member) => {
      if (clusterOf[member.dataSourceId] == null) clusterOf[member.dataSourceId] = { identity, idx };
    });
  });

  const members = Object.keys(clusterOf).map((id) => {
    const { identity, idx } = clusterOf[id];
    const member = (identity.dataSources || []).find((m) => String(m.dataSourceId) === String(id));
    return { ...member, identity, clusterIdx: idx };
  });

  // Table order: running/queued meta scans pin to the top, then most recently
  // profiled first (re-sorts as the per-source summaries stream in).
  const memberJobActive = (m) => {
    const pid = platformIdOf(m);
    return pid != null && activeJobByDataSource[pid] != null;
  };
  const profiledTs = (m) => {
    const at = summaries[m.dataSourceId]?.snapshotTakenAt;
    if (at == null) return 0;
    const n = typeof at === 'number' ? at : Date.parse(at);
    return Number.isFinite(n) ? n : 0;
  };
  members.sort((a, b) => {
    const runDelta = (memberJobActive(b) ? 1 : 0) - (memberJobActive(a) ? 1 : 0);
    if (runDelta !== 0) return runDelta;
    return profiledTs(b) - profiledTs(a);
  });

  // Search filter applied after the sort: matches database name, engine, host, and
  // the schema-identity/cluster labels. Empty term keeps everything.
  const term = searchTerm.trim().toLowerCase();
  const filteredMembers = term
    ? members.filter((m) =>
        [m.name, m.host, m.engine, engineLabels[m.engine], identityLabel(m.identity), commercialName(m.identity)]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      )
    : members;

  // Header-click sort overrides the default order; column values mirror what the cells render.
  const memberAccessors = {
    name: (m) => m.name,
    engine: (m) => engineLabels[m.engine] || m.engine,
    identity: (m) => commercialName(m.identity) || 'Custom',
    tables: (m) => {
      const n = Number(summaries[m.dataSourceId]?.tableCount);
      return Number.isFinite(n) ? n : null;
    },
    pk: (m) => {
      const tables = summaries[m.dataSourceId]?.tables;
      if (!Array.isArray(tables) || tables.length === 0) return null;
      return Math.round((tables.filter((t) => t.hasPrimaryKey).length / tables.length) * 100);
    },
    rows: (m) => {
      const n = Number(summaries[m.dataSourceId]?.totalRowCount);
      return Number.isFinite(n) ? n : null;
    },
    cluster: (m) => identityLabel(m.identity),
  };
  const visibleMembers = sortRows(filteredMembers, tableSort, memberAccessors);

  // A "cluster" needs more than one member data source; single-source identities are hidden
  // from the cards. The currently-expanded cluster (if any) drives the inline sources panel.
  const clusterList = (identities || []).filter((i) => (i.dataSources || []).length > 1);
  const expandedCluster = clusterList.find((i) => String(i.id) === String(expandedClusterId)) || null;

  if (loading && (!identities || identities.length === 0)) return null;

  if (!identities || identities.length === 0) {
    return (
      <div className='sg-empty-state m-3'>
        <div className='sg-empty-state__icon'>∅</div>
        <div className='sg-empty-state__title'>No schema identities yet</div>
        <div className='sg-empty-state__body'>
          Run a profile (Actions → New profile…) to fingerprint your data sources and cluster shared schemas.
        </div>
      </div>
    );
  }

  return (
    <div className='p-3'>
      {clusterList.length > 0 && (
        <>
          <h5 className='text-left sg-eyebrow'>Database identity clusters</h5>
          <div className='mb-3' style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {clusterList.map((identity) => {
              const idx = identities.indexOf(identity);
              const badge = confidenceBadge(identity);
              const expanded = String(expandedClusterId) === String(identity.id);
              return (
                <button
                  key={identity.id}
                  type='button'
                  onClick={() =>
                    setExpandedClusterId((prev) => (String(prev) === String(identity.id) ? null : identity.id))
                  }
                  className='sg-card text-left'
                  style={{
                    padding: '14px 16px',
                    cursor: 'pointer',
                    outline: expanded ? `2px solid ${chartSeriesColor(idx)}` : 'none',
                    outlineOffset: 1,
                  }}
                  data-testid={`cluster-card-${identity.id}`}
                  aria-expanded={expanded}
                >
                  <div className='d-flex align-items-center mb-1' style={{ gap: 8 }}>
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        background: chartSeriesColor(idx),
                        flex: 'none',
                      }}
                    />
                    <strong style={{ fontSize: 14 }}>{identityLabel(identity)}</strong>
                    <span
                      className='sg-pill sg-pill--nodot ml-auto'
                      style={{ background: badge.bg, color: badge.color, fontSize: 9.5 }}
                    >
                      {badge.label}
                    </span>
                  </div>
                  <div className='sg-small mb-2' style={{ color: 'var(--ink-500)' }}>
                    {commercialName(identity)
                      ? `Matches ${commercialName(identity)}`
                      : identity.primaryName
                        ? `Anchored on ${identity.primaryName}`
                        : 'Unmatched schema family'}
                  </div>
                  <div className='d-flex align-items-center' style={{ gap: 16 }}>
                    <span>
                      <strong>{(identity.dataSources || []).length}</strong>{' '}
                      <span className='sg-small' style={{ color: 'var(--ink-500)' }}>sources</span>
                    </span>
                    <span>
                      <strong>{fmt(identity.tables)}</strong>{' '}
                      <span className='sg-small' style={{ color: 'var(--ink-500)' }}>tables</span>
                    </span>
                    <span>
                      <strong>{identity.sizeGB}GB</strong>{' '}
                      <span className='sg-small' style={{ color: 'var(--ink-500)' }}>size</span>
                    </span>
                    <span className='sg-small ml-auto' style={{ color: chartSeriesColor(idx) }}>
                      {expanded ? 'Hide sources ▲' : 'Show sources ▼'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          {expandedCluster && (
            <Card className='mb-4' data-testid={`cluster-sources-${expandedCluster.id}`}>
              <Card.Body>
                <div className='d-flex align-items-center mb-2' style={{ gap: 8 }}>
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: chartSeriesColor(identities.indexOf(expandedCluster)),
                      flex: 'none',
                    }}
                  />
                  <strong>{identityLabel(expandedCluster)}</strong>
                  <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
                    {(expandedCluster.dataSources || []).length} data source
                    {(expandedCluster.dataSources || []).length === 1 ? '' : 's'}
                  </span>
                  <button
                    type='button'
                    onClick={() => setExpandedClusterId(null)}
                    className='ml-auto'
                    aria-label='Close'
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', fontSize: 16, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
                <table className='w-100' style={{ fontSize: 13, textAlign: 'left' }}>
                  <tbody>
                    {(expandedCluster.dataSources || []).map((member) => {
                      const platformId = platformIdOf(member);
                      return (
                        <tr key={member.dataSourceId} style={{ borderTop: '1px solid var(--ink-100)' }}>
                          <td style={{ padding: '8px 10px' }}>
                            {platformId != null ? (
                              <Link
                                to={`/metadata/source/${encodeURIComponent(platformId)}`}
                                style={{ fontFamily: FONT_MONO, color: BRAND.blue }}
                              >
                                {member.name}
                              </Link>
                            ) : (
                              <span style={{ fontFamily: FONT_MONO }}>{member.name}</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            <EngineIcon engine={member.engine} /> {engineLabels[member.engine] || member.engine || '—'}
                          </td>
                          <td style={{ padding: '8px 10px', fontFamily: FONT_MONO, color: 'var(--ink-500)' }}>
                            {member.host || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      <div className='d-flex align-items-center' style={{ gap: 12, flexWrap: 'wrap' }}>
        <h5 className='text-left sg-eyebrow mb-0'>
          Profiled databases — key elements{' '}
          <span style={{ color: 'var(--ink-400)', fontWeight: 400 }}>
            — what the meta scan learned before any discovery ran
          </span>
        </h5>
        <input
          data-testid='metadata-profile-search'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder='Search databases…'
          className='ml-auto mb-2'
          style={{
            minWidth: 220,
            borderRadius: 8,
            border: '1px solid var(--ink-200)',
            background: '#fff',
            padding: '6px 12px',
            fontSize: 13,
            outline: 'none',
          }}
        />
      </div>
      <Card>
        <Card.Body>
          {/* textAlign left beats the global `.App { text-align: center }` inherit. */}
          <table className='w-100' style={{ fontSize: 13, textAlign: 'left' }} data-testid='profiled-databases'>
            <thead>
              <tr className='sg-eyebrow' style={{ fontSize: 11, color: 'var(--ink-400)', textAlign: 'left' }}>
                <SortableTh label='Database' sortKey='name' sort={tableSort} onSort={setTableSort} />
                <SortableTh label='Engine' sortKey='engine' sort={tableSort} onSort={setTableSort} />
                <SortableTh label='Schema identity' sortKey='identity' sort={tableSort} onSort={setTableSort} />
                <SortableTh label='Tables · fields' sortKey='tables' sort={tableSort} onSort={setTableSort} />
                <SortableTh label='PK density' sortKey='pk' sort={tableSort} onSort={setTableSort} />
                <SortableTh label='Rows' sortKey='rows' sort={tableSort} onSort={setTableSort} />
                <SortableTh label='Cluster' sortKey='cluster' sort={tableSort} onSort={setTableSort} />
                <th style={{ padding: '8px 10px' }} />
              </tr>
            </thead>
            <tbody>
              {visibleMembers.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: '16px 10px', color: 'var(--ink-400)', textAlign: 'center' }}>
                    No profiled databases match “{searchTerm.trim()}”.
                  </td>
                </tr>
              )}
              {visibleMembers.map((member) => {
                const platformId = platformIdOf(member);
                const activeJob = platformId != null ? activeJobByDataSource[platformId] : null;
                if (activeJob) {
                  return (
                    <RunningJobRow
                      key={member.dataSourceId}
                      testId={`metadata-running-${member.dataSourceId}`}
                      nameNode={<span style={{ fontFamily: FONT_MONO }}>{member.name}</span>}
                      engine={member.engine}
                      jobType='Meta scan'
                      status={activeJob.status === 'PENDING' ? 'QUEUED' : 'RUNNING'}
                      completed={activeJob.workItemsCompleted}
                      total={activeJob.workItemsTotal}
                      unit={(activeJob.workItemKind || 'tables').toLowerCase()}
                      totalCols={8}
                      cancelling={!!(cancelling && cancelling[activeJob.id])}
                      onCancel={() => onCancelJob && onCancelJob(activeJob.id)}
                    />
                  );
                }
                const summary = summaries[member.dataSourceId];
                const tables = Array.isArray(summary?.tables) ? summary.tables : [];
                const fieldCount = tables.reduce((a, t) => a + (Number(t.columnCount) || 0), 0);
                const pkCount = tables.filter((t) => t.hasPrimaryKey).length;
                const pkPct = tables.length > 0 ? Math.round((pkCount / tables.length) * 100) : null;
                const product = commercialName(member.identity);
                return (
                  <tr key={member.dataSourceId} style={{ borderTop: '1px solid var(--ink-100)' }}>
                    <td style={{ padding: '9px 10px' }}>
                      {platformId != null ? (
                        <Link
                          to={`/metadata/source/${encodeURIComponent(platformId)}`}
                          style={{ fontFamily: FONT_MONO, color: BRAND.blue }}
                        >
                          {member.name}
                        </Link>
                      ) : (
                        <span style={{ fontFamily: FONT_MONO }}>{member.name}</span>
                      )}
                      <AdoptedFromBadge {...provenanceOf(member)} />
                      {summary?.snapshotTakenAt && (
                        <div className='sg-small' style={{ color: 'var(--ink-400)' }}>
                          profiled {getDisplayDate(summary.snapshotTakenAt)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                      <EngineIcon engine={member.engine} /> {engineLabels[member.engine] || member.engine || '—'}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ color: product ? STATUS.success.text : 'var(--ink-400)' }}>
                        {product || 'Custom'}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: FONT_MONO }}>
                      {summary ? `${fmt(summary.tableCount)} t · ${fmt(fieldCount)} f` : '—'}
                    </td>
                    <td style={{ padding: '9px 10px', minWidth: 110 }}>
                      {pkPct != null ? (
                        <div className='d-flex align-items-center' style={{ gap: 6 }}>
                          <div
                            style={{
                              flex: 1,
                              height: 6,
                              borderRadius: 'var(--r-full)',
                              background: INK[100],
                              overflow: 'hidden',
                            }}
                          >
                            <div style={{ width: `${pkPct}%`, height: '100%', background: BRAND.blue }} />
                          </div>
                          <span className='sg-small' style={{ fontFamily: FONT_MONO }}>{pkPct}%</span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--ink-400)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: FONT_MONO }}>
                      {summary ? rowsPretty(summary.totalRowCount) : '—'}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <span
                        className='sg-tag'
                        style={{ color: chartSeriesColor(member.clusterIdx), fontSize: 10.5 }}
                      >
                        {identityLabel(member.identity)}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button
                        size='sm'
                        style={{ background: BRAND.blue, borderColor: BRAND.blue }}
                        title='Run a metadata profile for this data source'
                        disabled={!!busy[`profile-${member.dataSourceId}`]}
                        onClick={() => run(member, 'profile')}
                      >
                        ↻ Profile
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card.Body>
      </Card>
    </div>
  );
}

export default MetaLandscape;
